import qc from 'qc'
import { emptyClipboard, setClipboard, ClipboardType } from './index'
import {
  AstEventItem,
  getTargetNodeAstConfig,
} from '@ps/bridge/qc/scripts/editor'
import store from '@ps/store'
import _ from 'lodash'
import { executeAction, getPasteNodeEventAction } from '@ps/workbench/undo-redo'
import {
  isVideo,
  isAudio,
  getSceneNodeByUuid,
  isConnectDrawLineNode,
  isFreeDrawLineNode,
  isCarouselNode,
} from '@ps/bridge/qc/base'
import { getVpAstFromSceneNode } from '@ps/bridge/qc/vp'
import { getVpConditionConf } from '@ps/bridge/qc/vp/config'
import { getEventItemGroupById } from '@ps/store/workbench/visual-program/utils'
import {
  findChildByRouteIndexInScene,
  getRouteIndexByNodeInScene,
} from '@ps/utils/game'

type GetEventParams = {
  node: qc.Node
  vpAst: AstEventItem[]
  eventId?: string
}

type CopyEventParams = {
  // 事件 id
  id: string
  // 节点
  node: qc.Node
  // 有条件的情况下，复制所有同类型的事件还是单个事件
  isAll?: boolean
}

type PasteEventParams = {
  // 目标节点
  targetNodes: qc.Node[]
  // 事件
  event?: any
  // 复制的源节点
  originalNode: qc.Node
  record?: boolean
}

type nodesAstValue = {
  [uuid: string]: AstEventItem[]
}

function getEventOnNode(ast: AstEventItem[], node: qc.Node) {
  const nodeAst = []
  for (const i in ast) {
    for (const j in ast[i].condition) {
      const condition = ast[i].condition[j]
      if (condition.node === node.uuid) {
        nodeAst.push(_.cloneDeep(ast[i]))
        break
      }
    }
  }
  return nodeAst
}
// 获取跨场景相同节点路径的节点
export function getSameRouteNodeBetweenScene(
  node: qc.Node,
  sceneNode: qc.Node,
): qc.Node | undefined {
  const path = getRouteIndexByNodeInScene(node)
  const newNode = findChildByRouteIndexInScene(sceneNode, path)
  if (newNode && newNode.name === node.name) {
    return newNode
  }
  return undefined
}

export function eventCanBePasted(
  targetNodes: qc.Node[],
  clipboardEvent: any,
): boolean {
  if (!clipboardEvent || clipboardEvent.length === 0) {
    return false
  }
  const vpConditionConf = getVpConditionConf()
  const conditionContent = clipboardEvent[0].condition[0].content
  // 场景才能粘贴条件比较
  if (clipboardEvent[0].condition[0].type === 'compare') {
    if (targetNodes.length === 0) {
      return true
    }
    return false
  }
  let conditions: string[] = []
  if (targetNodes.length === 0) {
    vpConditionConf['click'].forEach((item) => {
      // @ts-ignore
      conditions.push(item.content)
    })
    vpConditionConf['swipe'].forEach((item) => {
      // @ts-ignore
      conditions.push(item.content)
    })
    vpConditionConf['delay'].forEach((item) => {
      // @ts-ignore
      conditions.push(item.content)
    })
    vpConditionConf['customEvent'].forEach((item) => {
      // @ts-ignore
      conditions.push(item.content)
    })
    if (conditions.includes(conditionContent)) {
      return true
    }
    return false
  }
  const ans = targetNodes.every((node) => {
    conditions = []
    if (isVideo(node)) {
      vpConditionConf['video'].forEach((item) => {
        // @ts-ignore
        conditions.push(item.content)
      })
      // 音频的开始结束事件也可以复制粘贴到视频节点里
      vpConditionConf['audio'].forEach((item) => {
        // @ts-ignore
        conditions.push(item.content)
      })
      vpConditionConf['click'].forEach((item) => {
        // @ts-ignore
        conditions.push(item.content)
      })
      vpConditionConf['swipe'].forEach((item) => {
        // @ts-ignore
        conditions.push(item.content)
      })
      if (conditions.includes(conditionContent)) {
        return true
      }
      return false
    } else if (isAudio(node)) {
      vpConditionConf['audio'].forEach((item) => {
        // @ts-ignore
        conditions.push(item.content)
      })
      // 视频的开始结束事件也可以复制粘贴到音频节点里
      vpConditionConf['video'].forEach((item) => {
        // @ts-ignore
        conditions.push(item.content)
      })
      if (conditions.includes(conditionContent)) {
        return true
      }
      return false
    } else if (isConnectDrawLineNode(node)) {
      // 连线组件只允许粘贴画线开始和画线完成事件
      vpConditionConf['drawLine'].forEach((item) => {
        // @ts-ignore
        conditions.push(item.content)
      })
      if (conditions.includes(conditionContent)) {
        return true
      }
      return false
    } else if (isFreeDrawLineNode(node)) {
      // 自由画线组件只允许粘贴画线开始和画线完成事件
      vpConditionConf['freeDrawLine'].forEach((item) => {
        // @ts-ignore
        conditions.push(item.content)
      })
      if (conditions.includes(conditionContent)) {
        return true
      }
      return false
    } else if (isCarouselNode(node)) {
      // 允许轮播组件节点复制轮播事件
      vpConditionConf['carousel'].forEach((item) => {
        // @ts-ignore
        conditions.push(item.content)
      })
      if (conditions.includes(conditionContent)) {
        return true
      }
      return false
    }
    vpConditionConf['click'].forEach((item) => {
      // @ts-ignore
      conditions.push(item.content)
    })
    vpConditionConf['swipe'].forEach((item) => {
      // @ts-ignore
      conditions.push(item.content)
    })
    vpConditionConf['drag'].forEach((item) => {
      // @ts-ignore
      conditions.push(item.content)
    })
    if (conditions.includes(conditionContent)) {
      return true
    }
    return false
  })
  return ans
}

export function getEvent({ node, vpAst }: GetEventParams) {
  const config = getTargetNodeAstConfig(node.uuid, vpAst)
  return config
}

export function copyEvent({ id, node, isAll }: CopyEventParams) {
  emptyClipboard()
  if (!node) {
    node = store.state.workspace.sceneManager.currentSceneNode
  }
  const scene = getSceneNodeByUuid(node.uuid) as qc.Node
  const copyVpAst = getVpAstFromSceneNode(scene) as AstEventItem[]
  const ansEventAst = getEventItemGroupById(copyVpAst, id, isAll)
  const clipboard: ClipboardType = {
    originalNode: node,
    event: _.cloneDeep(ansEventAst),
  }
  setClipboard(clipboard)
}

export function pasteEvent({
  targetNodes,
  event,
  originalNode,
  record = true,
}: PasteEventParams) {
  const oriSceneNode = getSceneNodeByUuid(originalNode.uuid) as qc.Node
  if (targetNodes.length === 0) {
    targetNodes = [store.state.workspace.sceneManager.currentSceneNode]
  }
  const sceneNode = getSceneNodeByUuid(targetNodes[0].uuid) as qc.Node
  const targetVpAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
  const targetNodesAst: nodesAstValue = {}
  targetNodes.forEach((node) => {
    targetNodesAst[node.uuid] = getEventOnNode(targetVpAst, node)
  })
  let _targetNodes = targetNodes
  // 没有选中节点默认就是场景节点
  if (targetNodes.length === 0) {
    _targetNodes = [sceneNode]
  }
  executeAction({
    action: getPasteNodeEventAction({
      newValue: {
        copyScene: oriSceneNode,
        copyNodeAst: event,
        targetNodes: _targetNodes,
        sceneNode,
      },
      oldValue: { targetNodesAst, sceneNode },
    }),
    record,
  })
}

export function pasteEventByCopyNode({
  targetNodes,
  originalNode,
  record = true,
}: PasteEventParams) {
  const oriSceneNode = getSceneNodeByUuid(originalNode.uuid) as qc.Node
  const targetSceneNode = getSceneNodeByUuid(targetNodes[0].uuid) as qc.Node
  store.dispatch('workspace/game/pasteNodeEvent', {
    originalSceneNode: oriSceneNode,
    originalNodes: [originalNode],
    nodes: targetNodes,
    sceneNode: targetSceneNode,
    record: false,
  })
  if (
    originalNode.children.length > 0 &&
    targetNodes[0].children.length > 0 &&
    originalNode.children.length === targetNodes[0].children.length
  ) {
    originalNode.children.forEach((original, index) => {
      pasteEventByCopyNode({
        targetNodes: [targetNodes[0].children[index]],
        originalNode: original,
        record: false,
      })
    })
  }
}
